import {
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { listingsApi } from '~/entities/listing/api/listings-api'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { MINUTES } from '~/shared/lib/time-constants'
import type { CreateListingInput, UpdateListingInput } from '~/shared/types'

const LISTINGS_PAGE_SIZE = 20

/** Infinite-scroll listings list for a business. */
export function useListings(businessId: string) {
  const query = useInfiniteQuery({
    queryKey: queryKey(QueryKey.LISTINGS, businessId),
    queryFn: ({ pageParam }) =>
      listingsApi.list(businessId, {
        startIndex: pageParam,
        pageSize: LISTINGS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.window.nextStartIndex ?? undefined,
    staleTime: 2 * MINUTES,
    enabled: !!businessId,
  })

  const listings = useMemo(() => query.data?.pages.flatMap((p) => p.data) ?? [], [query.data])

  return {
    listings,
    total: query.data?.pages[0]?.window.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}

export function useListing(businessId: string, listingId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.LISTING, listingId),
    queryFn: () => listingsApi.getById(businessId, listingId),
    staleTime: 5 * MINUTES,
    enabled: !!businessId && !!listingId,
  })
}

/**
 * Returns the business's primary (first) listing - the one the dashboard's
 * "edit business" page actually edits. Pulled from listingsApi.list with
 * pageSize=1 so the request stays cheap regardless of how many listings the
 * business has. Returns null when no listing exists yet.
 *
 * Replaces reading listing-shaped fields off `useCurrentBusiness().data` -
 * that conflated business + listing data and produced "My Business"
 * placeholder + "123 Main St" location defaults for businesses without a
 * primary listing.
 */
export function usePrimaryListing(businessId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.LISTING, 'primary', businessId),
    queryFn: async () => {
      const page = await listingsApi.list(businessId, {
        startIndex: 0,
        pageSize: 1,
      })
      return page.data[0] ?? null
    },
    staleTime: 2 * MINUTES,
    enabled: !!businessId,
  })
}

export function useCreateListing(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateListingInput) => listingsApi.create(businessId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.LISTINGS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}

/** What an edited place makes stale: the place, the place list and the buttons its details decide. */
export function invalidateEditedListing(
  queryClient: QueryClient,
  businessId: string,
  listingId: string,
) {
  queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.LISTING, listingId) })
  queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.LISTINGS, businessId) })
  queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.LISTING_BUTTONS, listingId) })
  queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId) })
}

export function useUpdateListing(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ listingId, data }: { listingId: string; data: Partial<UpdateListingInput> }) =>
      listingsApi.update(businessId, listingId, data),
    onSuccess: (_data, { listingId }) =>
      invalidateEditedListing(queryClient, businessId, listingId),
  })
}

export function usePublishListing(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (listingId: string) => listingsApi.publish(businessId, listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.LISTINGS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}

export function useDeleteListing(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (listingId: string) => listingsApi.delete(businessId, listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.LISTINGS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}
